import React, {
  useCallback, useEffect, useMemo, useRef, useState, RefObject, ReactElement,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ClickAwayListener from '../../../components/click-away-listener/click-away-listener.component';
import SearchQuickMenu from '../../../components/search-quick-menu/search-quick-menu.component';
import SearchFieldBlock from '../../../components/search-field-block/search-field-block.component';
import SearchBarPlainContainer
  from '../../../components/search-bar-plain-container/search-bar-plain-container.component';
import Form from '../../../components/form/form.component';
import FormContainer from '../../../components/form-container/form-container.component';
import ScreenService from '../../../services/screen-service';
import FormatService from '../../../services/format-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import { getSearchQuickResultData, resetSearchQuickResultData } from '../../search/search.action';
import { toggleRightDrawer } from '../right-drawer/right-drawer.action';
import toggleNavigationDrawer from '../navigation/navigation.action';
import { FORM_ID_SEARCH_BAR } from '../../../constants/system/form-ids';
import { SEARCH_RESULT_VIEW } from '../../../constants/entity/permissions';
import {
  KEYBOARD_KEY_ARROW_DOWN, KEYBOARD_KEY_BACKSPACE, KEYBOARD_KEY_ENTER, KEYBOARD_KEY_SPACE,
} from '../../../constants/system/keyboard-keys';
import { REGEXP_TEMPLATE_ALL_NAME_SYMBOLS } from '../../../constants/system/regexp-templates';
import { ROUTE_CONTACT, ROUTE_DEAL, ROUTE_SEARCH } from '../../../constants/system/routes';
import { QUERY_PARAM_QUERY } from '../../../constants/system/query-params';
import {
  SEARCH_QUICK_TYPE_COMPANY, SEARCH_QUICK_TYPE_DEAL, SEARCH_QUICK_TYPE_PERSON,
} from '../../../constants/system/search-quick-types';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const SearchBar: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const SEARCH_QUICK_MIN_LENGTH: number = 3;
  const SEARCH_QUERY_FIELD_NAME: string = 'query';

  const {
    isPlain,
    form: formProps,
    handleSubmit: submitForm,
    values: formValues,
  } = props;

  const {
    change: changeForm,
  } = formProps;

  const dispatch: ReduxDispatch = useDispatch();
  const location: ReactRouterLocation = useLocation();
  const navigate: ReactRouterNavigate = useNavigate();
  const staticSearchRef: RefObject<HTMLDivElement> | null = useRef(null);
  const floatSearchRef: RefObject<HTMLDivElement> | null = useRef(null);
  const isNavigationDrawerOpen: boolean = useSelector(({ navigation }: Reducers): boolean => (
    navigation.isDrawerOpen
  ));
  const isRightDrawerOpen: boolean = useSelector(({ rightDrawer }: Reducers): boolean => (
    rightDrawer.isDrawerOpen
  ));
  const searchQuickResultData: SearchQuickResultData = useSelector(({ search }: Reducers): SearchQuickResultData => (
    search.searchQuickResultData
  ));
  const isSmallScreen: boolean = useScreenSmall();

  const currentPathname: string = location.pathname;
  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const searchQuery: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_QUERY]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_QUERY].toString()) : null;
  const searchQuickResultList: SearchQuickResultItem[] = searchQuickResultData
  && searchQuickResultData.searchQuickResult ? searchQuickResultData.searchQuickResult : [];

  const TYPE_TO_ROUTE_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [SEARCH_QUICK_TYPE_COMPANY]: ROUTE_CONTACT,
    [SEARCH_QUICK_TYPE_PERSON]: ROUTE_CONTACT,
    [SEARCH_QUICK_TYPE_DEAL]: ROUTE_DEAL,
  }), []);

  const TYPE_TO_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [SEARCH_QUICK_TYPE_COMPANY]: 'Company',
    [SEARCH_QUICK_TYPE_PERSON]: 'Person',
    [SEARCH_QUICK_TYPE_DEAL]: 'Deal',
  }), []);

  const TYPE_TO_DEFAULT_IMAGE_MAP: Record<string, ReactElement> = useMemo((): Record<string, ReactElement> => ({
    [SEARCH_QUICK_TYPE_COMPANY]: CompanyDefaultImage,
    [SEARCH_QUICK_TYPE_PERSON]: PersonDefaultImage,
    [SEARCH_QUICK_TYPE_DEAL]: AssetDefaultImage,
  }), []);

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);

  const searchResultViewPermissions: string[] = useMemo((): string[] => [SEARCH_RESULT_VIEW], []);

  const transformSearchBar: Function = useCallback((): void => {
    if (floatSearchRef.current) {
      const searchForm: SearchFormElement | null = ScreenService.getElementById(FORM_ID_SEARCH_BAR);

      if (searchForm && searchForm[SEARCH_QUERY_FIELD_NAME]) {
        searchForm[SEARCH_QUERY_FIELD_NAME].blur();
      }

      setMenuAnchor(null);
    }
  }, [floatSearchRef]);

  const sendForm: Function = useCallback((values: FormValues): void => {
    const queryValue: string | null = values[SEARCH_QUERY_FIELD_NAME] ? values[SEARCH_QUERY_FIELD_NAME].trim() : null;

    if (!queryValue) {
      return;
    }

    changeForm(SEARCH_QUERY_FIELD_NAME, queryValue);

    setMenuAnchor(null);

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams({
        [QUERY_PARAM_QUERY]: queryValue,
      }),
    });
  }, []);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    sendForm(formValues);
  }, [formValues]);

  const onClickAway: ClickAwayFunction = useCallback((): void => {
    transformSearchBar();
  }, [floatSearchRef]);

  const onKeyDown: Function = useCallback((event: React.KeyboardEvent): void => {
    if (event.key === KEYBOARD_KEY_ENTER) {
      const searchRef: HTMLDivElement | null = floatSearchRef.current
        ? floatSearchRef.current : staticSearchRef.current;
      const queryField: HTMLInputElement | null = (searchRef?.children[0]?.children[0] as HTMLInputElement) || null;

      event.preventDefault();

      transformSearchBar();

      sendForm({
        query: queryField ? queryField.value : '',
      });
    }
  }, [floatSearchRef, staticSearchRef]);

  const processQuickSearch: Function = useCallback((
    event: React.SyntheticEvent,
    isForced?: boolean,
  ): Promise<ActionCallback> | null => {
    const searchRef: HTMLDivElement | null = floatSearchRef.current
      ? floatSearchRef.current : staticSearchRef.current;
    const queryField: HTMLInputElement | null = (searchRef?.children[0]?.children[0] as HTMLInputElement) || null;
    const queryValue: string | null = queryField ? queryField.value : null;

    if (queryValue && queryValue.length >= SEARCH_QUICK_MIN_LENGTH && (queryValue.trim() === queryValue || isForced)) {
      setMenuAnchor(event.currentTarget);

      return new Promise((resolve: ActionResolveCallback): void => {
        dispatch(getSearchQuickResultData({
          query: queryValue.trim(),
        }, resolve));
      });
    }

    return null;
  }, [floatSearchRef, staticSearchRef]);

  const onKeyUp: Function = useCallback((event: React.KeyboardEvent): Promise<ActionCallback> | null => {
    const isArrowDownKey: boolean = event.key === KEYBOARD_KEY_ARROW_DOWN;
    const isBackspaceKey: boolean = event.key === KEYBOARD_KEY_BACKSPACE;
    const isSpaceKey: boolean = event.key === KEYBOARD_KEY_SPACE;
    const isCtrlKeyPressed: boolean = event.ctrlKey;
    const isForced: boolean = isArrowDownKey || (isSpaceKey && isCtrlKeyPressed);
    const isAllowedSearchKey: boolean = (new RegExp(REGEXP_TEMPLATE_ALL_NAME_SYMBOLS)).test(event.key)
      || isBackspaceKey;

    if (isAllowedSearchKey || isForced) {
      return processQuickSearch(event, isForced);
    }

    dispatch(resetSearchQuickResultData());

    setMenuAnchor(null);

    return null;
  }, [floatSearchRef, staticSearchRef, searchQuickResultData]);

  const closeActionMenu: CloseActionMenuHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const onPointerDown: Function = useCallback((event: React.PointerEvent): Promise<ActionCallback> | null => {
    if (isSmallScreen) {
      if (isNavigationDrawerOpen) {
        dispatch(toggleNavigationDrawer());
      }

      if (isRightDrawerOpen) {
        dispatch(toggleRightDrawer());
      }
    }

    event.stopPropagation();

    if (!searchQuickResultData && !searchQuery) {
      return processQuickSearch(event);
    }

    return null;
  }, [
    floatSearchRef, staticSearchRef, searchQuickResultData, searchQuery, isSmallScreen,
    isNavigationDrawerOpen, isRightDrawerOpen,
  ]);

  const onSearchQuickResultItemClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, searchQuickResultItem: SearchQuickResultItem,
  ): void => {
    if (searchQuickResultItem.entityType && searchQuickResultItem.entityId) {
      const entityRoute: string | null = TYPE_TO_ROUTE_MAP[searchQuickResultItem.entityType] || null;

      if (entityRoute) {
        navigate({
          pathname: `${entityRoute}/${searchQuickResultItem.entityId}`,
        });

        setMenuAnchor(null);
      }
    }

    event.stopPropagation();
  }, []);

  useEffect((): void => {
    if (searchQuery) {
      changeForm(SEARCH_QUERY_FIELD_NAME, searchQuery.trim());
    }
  }, [searchQuery]);

  useEffect((): void => {
    if (currentPathname !== ROUTE_SEARCH) {
      if (searchQuickResultData) {
        dispatch(resetSearchQuickResultData());
      }

      changeForm(SEARCH_QUERY_FIELD_NAME, '');
    }
  }, [currentPathname]);

  useEffect((): void => {
    setMenuAnchor(null);
  }, [isSmallScreen]);

  return (
    <CheckAccess permissions={searchResultViewPermissions}>
      <Form id={FORM_ID_SEARCH_BAR}>
        {
          !isPlain && (
            <FlexContainer isInline>
              {
                isSmallScreen && (
                  <ClickAwayListener onClickAway={onClickAway}>
                    <SearchFieldBlock
                      isFloat
                      isSimple
                      withNoButton
                      ref={floatSearchRef}
                      searchFieldName={SEARCH_QUERY_FIELD_NAME}
                      onKeyDown={onKeyDown}
                      onKeyUp={onKeyUp}
                      onPointerDown={onPointerDown}
                      onSubmitForm={onSubmitForm}
                    />
                  </ClickAwayListener>
                )
              }
              {
                !isSmallScreen && (
                  <SearchFieldBlock
                    ref={staticSearchRef}
                    searchFieldName={SEARCH_QUERY_FIELD_NAME}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    onPointerDown={onPointerDown}
                    onSubmitForm={onSubmitForm}
                  />
                )
              }
            </FlexContainer>
          )
        }
        {
          isPlain && (
            <SearchBarPlainContainer>
              <SearchFieldBlock
                isPlain
                isSimple
                ref={staticSearchRef}
                searchFieldName={SEARCH_QUERY_FIELD_NAME}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                onPointerDown={onPointerDown}
                onSubmitForm={onSubmitForm}
              />
            </SearchBarPlainContainer>
          )
        }
        <SearchQuickMenu
          typeLabelMap={TYPE_TO_LABEL_MAP}
          typeDefaultImageMap={TYPE_TO_DEFAULT_IMAGE_MAP}
          menuAnchor={menuAnchor}
          searchResultItemList={searchQuickResultList}
          onCloseActionMenu={closeActionMenu}
          onSearchQuickResultItemClick={onSearchQuickResultItemClick}
        />
      </Form>
    </CheckAccess>
  );
});

const SearchBarForm: ReactComponent = (props: ReactComponentProps): ReactElement => (
  <FormContainer
    formId={FORM_ID_SEARCH_BAR}
    component={SearchBar}
    {...props}
  />
);

export default SearchBarForm;
